import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router';
import { AppShell } from '@/widgets/AppShell';
import { VolunteersPage } from '@/pages/VolunteersPage';
import { MissionsPage } from '@/pages/MissionsPage';
import { MissionDetailPage } from '@/pages/MissionDetailPage';
import { CommitteePage } from '@/pages/CommitteePage';
import { AuditPage } from '@/pages/AuditPage';
import { ExportsPage } from '@/pages/ExportsPage';
import { StatisticsPage } from '@/pages/StatisticsPage';
import { BrandingPage } from '@/pages/BrandingPage';
import { ThemesPage } from '@/pages/ThemesPage';
import { PointsPage } from '@/pages/PointsPage';
import { CommitteeSettingsPage } from '@/pages/CommitteeSettingsPage';
import { UsersPage } from '@/pages/UsersPage';


/**
 * Code-based routes, not file-based.
 *
 * The file-based router needs a Vite plugin and a generated route tree committed to the repo. With
 * four routes that is a build step, a generated artefact and a second source of truth in exchange for
 * nothing — §18.11 prefers deleting structure to adding speculative structure. If this grows past a
 * dozen routes the generator earns its place.
 */
const rootRoute = createRootRoute({
  component: AppShell,
});

/**
 * The panel opens on the review queue rather than on a landing page, because an unreviewed volunteer
 * is the only thing here that blocks a person from being sent to a search.
 */
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/volunteers' });
  },
});

const volunteersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/volunteers',
  component: VolunteersPage,
});

/**
 * Appointing people to roles (§23.1).
 *
 * ⚠️ Administration only, and the SERVER decides that — every call this page makes is behind the
 * Admin policy, so an operator without it gets 403 and sees the API's own sentence. The route is
 * not hidden by the panel: §12.2 keeps visibility data-driven, and a panel that hides a screen on
 * its own idea of a role drifts from the server the first time a policy changes.
 */
const usersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users',
  component: UsersPage,
})

const auditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/audit',
  component: AuditPage,
});

/**
 * The workbooks the association takes away (§19).
 *
 * ⚠️ Administration only, and the SERVER decides that — like `/users` above, the route is not hidden
 * by the panel (§12.2 keeps visibility data-driven), and an operator without the policy gets the
 * API's own 403.
 */
const exportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/exports',
  component: ExportsPage,
});

const brandingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/branding',
  component: BrandingPage,
});

/**
 * The palette itself (§7).
 *
 * ⚠️ Beside `/branding` rather than inside it: that screen writes the four image marks of whatever
 * theme is live, and this one prepares a palette nobody is looking at yet and then publishes it.
 * Folding them together would put "changes nothing until you activate it" and "changes the
 * certificate everybody prints" behind the same save button.
 */
const themesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/themes',
  component: ThemesPage,
});

const pointsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/points',
  component: PointsPage,
});

const committeeSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/committee-settings',
  component: CommitteeSettingsPage,
});

/**
 * The association's numbers (§19).
 *
 * ⚠️ Its own route rather than a strip on the missions board: the roster half of this screen answers
 * "right now" and has nothing to do with any one search, and folding it into a mission list would
 * make the figures look like that list's totals.
 */
const statisticsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/statistics',
  component: StatisticsPage,
});

const missionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/missions',
  component: MissionsPage,
});

/**
 * One mission in full. A route rather than an expanded row: §23.6 credits participation from this
 * screen, and a decision that takes reading five tables needs a URL an operator can send to the Head
 * of Control rather than a row somebody has to find again.
 */
const missionDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/missions/$missionId',
  component: function MissionDetailRoute() {
    const { missionId } = missionDetailRoute.useParams();
    return <MissionDetailPage missionId={missionId} />;
  },
});

const committeeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/committee',
  component: CommitteePage,
});


export const routeTree = rootRoute.addChildren([
  indexRoute,
  volunteersRoute,
  missionsRoute,
  missionDetailRoute,
  committeeRoute,
  usersRoute,
  auditRoute,
  exportsRoute,
  statisticsRoute,
  pointsRoute,
  brandingRoute,
  themesRoute,
  committeeSettingsRoute,
]);

/**
 * ⚠️ **The router has to be told where the panel lives, and it was not.**
 *
 * Vite builds with `base: '/admin/'` (§14 — everything the association runs answers on one host,
 * with paths beneath it), so the dev server and Caddy both serve the application from `/admin/`.
 * The router, given no basepath, matched `location.pathname` from the site root: at `/admin/users`
 * it looked for a route called `/admin/users`, found none, and rendered Not Found. Every route in
 * the panel was unreachable by URL — typed, pasted, bookmarked or refreshed — while clicking a link
 * still worked, because a click never leaves the router.
 *
 * Read from `BASE_URL` rather than written as `/admin`, so the two cannot drift: change `base` in
 * `vite.config.ts` and this follows.
 */
export const router = createRouter({ routeTree, basepath: import.meta.env.BASE_URL });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}


